import './user-playlists.scss';
import {useState, MutableRefObject, useRef, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {PlaylistType} from '../../backend/enums/playlist-type';
import {Playlist} from '../../backend/interfaces/playlist';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {usePaginatedTable} from '../../hooks/paginated-table';
import {translations} from '../../i18n/translations';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../repositories/playlists';
import {playlistTypes} from '../../utils/data/playlist-types';
import {PageHeader} from '../page-header/page-header';
import {PaginatedTable} from '../paginated-table/paginated-table';
import {SelectInput} from '../select-input/select-input';
import {Alert, Box, Stack} from '@mui/material';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {LoadingContent} from '../loading-content/loading-content';
import {UsersRepository} from '../../repositories/users';
import GenericHeader from '../generic-header/generic-header';
import SearchInput from '../search-input/search-input';
import {PlaylistsMenu} from './playlists-menu';
import {PlaylistStateModal} from './modal/playlist-state-modal';

// Hold variables
const BATCH_SIZE: number = 10;

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  State = 'state',
}

type UserPlaylistsTableProps = {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  canWrite: boolean;
  userId: string;
  userName: string;
  playlistType: PlaylistType;
  entity?: {
    entityId: string;
    entityName?: string;
  };
  filters?: React.ReactNode;
  getPlaylists: (
    limit: number,
    skip: number,
    search?: string,
    playlistType?: PlaylistType
  ) => Promise<{
    data: PaginatedTableData<Playlist>;
    totalCount: number;
  }>;
};
const UserPlaylistsTable = ({
  caregiverMode,
  canWrite,
  userId,
  userName,
  playlistType,
  entity,
  filters,
  getPlaylists,
}: UserPlaylistsTableProps) => {
  const snackBarContext = useSnackBar();
  const [updatePlaylistDialogOpened, setUpdatePlaylistDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});
  const playlistTypeRef: MutableRefObject<PlaylistType> = useRef(playlistType);
  const entityRef: MutableRefObject<
    | {
        entityId: string;
        entityName?: string;
      }
    | undefined
  > = useRef(entity);
  const dataToUpdateRef: MutableRefObject<
    {playlistId: string; playlistName: string; playlistActiveFlag: boolean} | undefined
  > = useRef();
  const {t} = useTranslation();

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateItem,
    handleUpdateCurrentSearch,
  } = usePaginatedTable<Playlist>({
    fetchData: (limit: number, skip: number, search?: string) =>
      getPlaylists(limit, skip, search, playlistTypeRef.current),
  });

  // Update the data when the entity or the playlist type change
  useEffect(() => {
    if (entity?.entityId !== entityRef.current?.entityId) {
      // Reset
      entityRef.current = entity;

      handleApplyFilters(1, currentSearch);
    }

    if (playlistType !== playlistTypeRef.current) {
      // Update the playlist type
      playlistTypeRef.current = playlistType;

      // Fetch data and reset page count
      handleApplyFilters(1);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, playlistType]);

  const handleAskUpdatePlaylistState = (playlistId: string, value: boolean) => {
    // Get playlist name
    const playlistName: string | undefined = data
      .find((obj) => obj.id === playlistId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set playlist info for later
    dataToUpdateRef.current = {
      playlistId: playlistId,
      playlistActiveFlag: value,
      playlistName: playlistName || '',
    };

    // Open modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdatePlaylistState = () => {
    // Close modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdatePlaylistState = () => {
    // Close modal
    setUpdatePlaylistDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdatePlaylistState();
  };

  // Enable/disable playlist
  const handleUpdatePlaylistState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const playlistId = dataToUpdateRef.current.playlistId;
      const value = dataToUpdateRef.current.playlistActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === playlistId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        playlistId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.user_playlists.state_value_true)
              : t(translations.user_playlists.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it can take some time to update
      continueUpdatePlaylistState(userId || '', playlistId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_playlists.errors.update_state_playlist_error_message),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdatePlaylistState = async (
    userId: string,
    playlistId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistActiveStateForUser(
        userId,
        playlistId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === playlistId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        playlistId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.user_playlists.state_value_true)
              : t(translations.user_playlists.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(translations.user_playlists.errors.enable_playlist_error_message)
          : t(translations.user_playlists.errors.disable_playlist_error_message),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist with id ${playlistId} to ${value}.`,
        error
      );
    }
  };

  const modalState: 'disable' | 'enable' = updatePlaylistDialogOpened.disableDialogOpened
    ? 'disable'
    : 'enable';

  return (
    <>
      {caregiverMode === CaregiverMode.SystemAdministrator && (
        <PageHeader
          showSearch={true}
          searchLabel={t(translations.user_playlists.search_input_placeholder)}
          searchText={currentSearch}
          searchOnChange={(text: string) => handleUpdateCurrentSearch(text)}
          searchOnSubmit={() => handleApplyFilters(1, currentSearch)}
          disableSearch={isLoading}
          filters={filters}
        />
      )}
      <PaginatedTable
        avatar={{
          type: 'other',
        }}
        emptyTableMessage={t(translations.user_playlists.no_playlists)}
        columns={[
          {
            columnId: ColumnIdentifier.Name,
            title: t(translations.user_playlists.name_header),
            flexGrow: 2,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(translations.user_playlists.state_header),
            flexGrow: 1,
            button: {
              type: 'toggle',
              showColumnValue: true,
              toggle: {
                enabledValue: t(translations.user_playlists.state_value_true),
                handleClick: handleAskUpdatePlaylistState,
              },
              disabled: !canWrite,
              disabledTooltip: t(translations.user_playlists.state_disabled_tooltip),
            },
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={true}
      />
      <PlaylistStateModal
        open={
          updatePlaylistDialogOpened.disableDialogOpened ||
          updatePlaylistDialogOpened.enableDialogOpened
        }
        onClose={handleDeclineUpdatePlaylistState}
        onConfirm={handleConfirmUpdatePlaylistState}
        state={modalState}
        playlistName={dataToUpdateRef.current?.playlistName || ''}
        caregiverMode={caregiverMode}
        userName={userName}
        entityName={entityRef.current?.entityName || ''}
      />
    </>
  );
};

/**
 * Listing of all the playlists associated with a user.
 * On this screen, it's possible to enable/disable a playlist.
 *
 * System admins have an affiliation filter to get the playlists.
 *
 * Permissions are given by props.
 */
type UserPlaylistsProps = {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  canWrite: boolean;
  userId: string;
  userName: string;
  playlistRowBaseLinkUrl: string;
  entityId?: string;
  headerTitleCrumbs?: {label: string; link: string}[];
  headerTitle?: string;
};
export const UserPlaylists = ({
  caregiverMode,
  canWrite,
  userId,
  userName,
  playlistRowBaseLinkUrl,
  entityId,
  headerTitleCrumbs,
  headerTitle,
}: UserPlaylistsProps) => {
  const [playlistType, setPlaylistType] = useState<PlaylistType>(PlaylistType.Karaoke);
  const [userAffiliations, setUserAffiliations] = useState<
    | {
        affiliations: Array<AffiliationInfo>;
        totalAffiliations: number;
        selectedAffiliation?: AffiliationInfo;
      }
    | undefined
  >();
  const {t} = useTranslation();

  // Get the first batch of user affiliations
  useEffect(() => {
    const getUserAffiliations = async () => {
      try {
        // Get first batch of affiliations
        const result = await UsersRepository.instance.getUserAffiliations(userId, 0, BATCH_SIZE);

        // Set affiliations
        if (result) {
          setUserAffiliations({
            affiliations: result.data,
            selectedAffiliation: result.data[0],
            totalAffiliations: result.totalCount,
          });
        } else {
          // Error
          throw new Error('Empty user affiliations.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get the user affiliations.', error);

        // Set user affiliations
        setUserAffiliations({
          affiliations: [],
          totalAffiliations: 0,
        });
      }
    };

    if (caregiverMode === CaregiverMode.SystemAdministrator) {
      getUserAffiliations();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caregiverMode, userId]);

  // Change the playlist type
  const handleChangePlaylistType = (value: string) => {
    if (value !== playlistType) {
      // Update the playlist type
      setPlaylistType(value as PlaylistType);
    }
  };

  // Fetch playlists
  const handleGetPlaylists = async (
    limit: number,
    skip: number,
    search?: string,
    playlistType?: PlaylistType
  ) => {
    const id = entityId
      ? entityId
      : userAffiliations?.selectedAffiliation
        ? userAffiliations.selectedAffiliation.entity.id
        : '';

    if (id === '') {
      return {
        data: [],
        totalCount: 0,
      };
    } else if (!playlistType) {
      console.warn('Empty playlist type.');
      return {
        data: [],
        totalCount: 0,
      };
    }

    // Load playlists
    const result: {totalCount: number; data: Array<Playlist>} =
      await PlaylistsRepository.instance.getPlaylistsForUserInEntity(
        userId,
        id,
        skip,
        limit,
        playlistType,
        true,
        search
      );

    // Process data
    const processedData: PaginatedTableData<Playlist> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        avatarUrl: obj.imageUrl,
        toLink:
          playlistRowBaseLinkUrl.charAt(playlistRowBaseLinkUrl.length - 1) === '/'
            ? `${playlistRowBaseLinkUrl}${obj.id}`
            : `${playlistRowBaseLinkUrl}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            // The name is localized
            value: obj.name || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.user_playlists.state_value_true)
              : t(translations.user_playlists.state_value_false),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {currentSearch, handleApplyFilters, handleUpdateCurrentSearch} =
    usePaginatedTable<Playlist>({
      fetchData: (limit, skip, search) => handleGetPlaylists(limit, skip, search, playlistType),
    });

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {entityId && (headerTitleCrumbs || headerTitle) && (
        <GenericHeader
          pageTitle={headerTitle || ''}
          titleCrumbs={headerTitleCrumbs ? headerTitleCrumbs : []}
          isLoading={false}
          leftColumn={
            <Stack
              gap={2}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                width: '100%',
                paddingTop: '10px',
              }}
            >
              <SearchInput
                placeholder={t(translations.user_playlists.search_input_placeholder)}
                value={currentSearch}
                onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
                onSearchAction={(value: string) => handleApplyFilters(1, value)}
              />
            </Stack>
          }
          rightColumn={
            <PlaylistsMenu
              playlistType={playlistType}
              onChangePlaylistType={handleChangePlaylistType}
            />
          }
        />
      )}
      <Box
        className={`playlists-listing-container${caregiverMode === CaregiverMode.SystemAdministrator ? '-sys' : ''}`}
      >
        <Box className="playlists-listing-container-centered">
          {caregiverMode === CaregiverMode.SystemAdministrator && !userAffiliations && (
            <LoadingContent marginTop="32px" />
          )}
          {((caregiverMode === CaregiverMode.Professional && !entityId) ||
            (caregiverMode === CaregiverMode.SystemAdministrator &&
              userAffiliations &&
              userAffiliations.affiliations.length === 0)) && (
            <Alert severity="info" className="user-playlists-info-message">
              {t(translations.user_playlists.no_affiliations_info_message)}
            </Alert>
          )}
          {((caregiverMode === CaregiverMode.Professional && entityId) ||
            (caregiverMode === CaregiverMode.SystemAdministrator &&
              userAffiliations &&
              userAffiliations.affiliations.length > 0 &&
              userAffiliations.selectedAffiliation)) && (
            <UserPlaylistsTable
              caregiverMode={caregiverMode}
              canWrite={canWrite}
              userId={userId}
              userName={userName}
              entity={
                caregiverMode === CaregiverMode.SystemAdministrator
                  ? userAffiliations?.selectedAffiliation?.entity.id
                    ? {
                        entityId: userAffiliations.selectedAffiliation.entity.id,
                        entityName: userAffiliations.selectedAffiliation.entity.name,
                      }
                    : undefined
                  : entityId
                    ? {entityId: entityId}
                    : undefined
              }
              playlistType={playlistType}
              filters={
                <Box className="user-playlists-select-input-container">
                  <SelectInput
                    className={
                      caregiverMode === CaregiverMode.SystemAdministrator
                        ? 'user-playlists-select-input-big'
                        : 'user-playlists-select-input-small'
                    }
                    label={t(translations.user_playlists.type_filter)}
                    items={playlistTypes.map((obj) => {
                      return {
                        value: obj.type,
                        label: t(obj.translation),
                      };
                    })}
                    value={playlistType}
                    handleChange={handleChangePlaylistType}
                    inputSize="small"
                  />
                </Box>
              }
              getPlaylists={handleGetPlaylists}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
